import { useState, useEffect, useCallback } from 'react';

const DISMISS_KEY = 'biometric_dismiss';

interface BiometricData {
  credentialId: string; // base64
  password: string;     // base64
}

const storageKeyFor = (houseId: string) => `biometric_${houseId}`;

const getStored = (houseId: string): BiometricData | null => {
  try {
    const raw = localStorage.getItem(storageKeyFor(houseId));
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
};

const randomBytes = (n: number): ArrayBuffer => {
  const buf = new ArrayBuffer(n);
  crypto.getRandomValues(new Uint8Array(buf));
  return buf;
};

const toBase64 = (buf: ArrayBuffer): string =>
  btoa(Array.from(new Uint8Array(buf), b => String.fromCharCode(b)).join(''));

const fromBase64 = (str: string): ArrayBuffer => {
  const binary = atob(str);
  const buf = new ArrayBuffer(binary.length);
  const arr = new Uint8Array(buf);
  for (let i = 0; i < binary.length; i++) arr[i] = binary.charCodeAt(i);
  return buf;
};

const checkAvailable = (): Promise<boolean> => {
  if (typeof window === 'undefined' || !window.PublicKeyCredential) return Promise.resolve(false);
  return PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable().catch(() => false);
};

export const useBiometric = (houseId: string) => {
  const [isAvailable, setIsAvailable] = useState(false);
  const [isEnrolled, setIsEnrolled] = useState(() => getStored(houseId) !== null);
  const [isPromptDismissed, setIsPromptDismissed] = useState(() => localStorage.getItem(DISMISS_KEY) === '1');

  useEffect(() => { checkAvailable().then(setIsAvailable); }, []);

  const enroll = useCallback(async (password: string): Promise<boolean> => {
    try {
      const credential = await navigator.credentials.create({
        publicKey: {
          challenge: randomBytes(32),
          rp: { name: 'EasyHome', id: window.location.hostname },
          user: { id: randomBytes(16), name: 'user', displayName: 'EasyHome User' },
          pubKeyCredParams: [{ type: 'public-key', alg: -7 }, { type: 'public-key', alg: -257 }],
          authenticatorSelection: { authenticatorAttachment: 'platform', userVerification: 'required' },
          timeout: 60000,
        },
      }) as PublicKeyCredential | null;
      if (!credential) return false;
      // TODO: password is stored as plain base64 — readable by anyone with localStorage access.
      // When WebAuthn PRF extension has wider browser support, use it to derive an encryption
      // key from the credential and encrypt the password before storing it.
      const data: BiometricData = { credentialId: toBase64(credential.rawId), password: btoa(password) };
      localStorage.setItem(storageKeyFor(houseId), JSON.stringify(data));
      setIsEnrolled(true);
      return true;
    } catch {
      return false;
    }
  }, [houseId]);

  const authenticate = useCallback(async (): Promise<string | null> => {
    const stored = getStored(houseId);
    if (!stored) return null;
    try {
      const result = await navigator.credentials.get({
        publicKey: {
          challenge: randomBytes(32),
          allowCredentials: [{ id: fromBase64(stored.credentialId), type: 'public-key' }],
          userVerification: 'required',
          timeout: 60000,
        },
      });
      if (!result) return null;
      return atob(stored.password);
    } catch {
      return null;
    }
  }, [houseId]);

  // Call after a successful password login to keep stored password in sync
  const updateStoredPassword = useCallback((password: string) => {
    const stored = getStored(houseId);
    if (!stored) return;
    localStorage.setItem(storageKeyFor(houseId), JSON.stringify({ ...stored, password: btoa(password) }));
  }, [houseId]);

  const unenroll = useCallback(() => {
    localStorage.removeItem(storageKeyFor(houseId));
    setIsEnrolled(false);
  }, [houseId]);

  const dismissPrompt = useCallback(() => {
    localStorage.setItem(DISMISS_KEY, '1');
    setIsPromptDismissed(true);
  }, []);

  return { isAvailable, isEnrolled, isPromptDismissed, enroll, authenticate, updateStoredPassword, unenroll, dismissPrompt };
};
